import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Facebook, Instagram, MapPin, Quote, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { SOINS, QUICK_SOINS, TEMOIGNAGES, TEMOIGNAGES_DEFILANT, QUI_SUIS_JE, CABINETS } from "../data/content";
import { Reveal, LineReveal } from "../components/Reveal";
import EditorialMarquee from "../components/Marquee";
import { NewsletterSignup } from "../components/NewsletterSignup";

const CHAPTERS = [
  { num: "01", title: "Écouter", text: "Chaque séance commence par une écoute attentive de votre histoire, de votre corps et de vos besoins du moment." },
  { num: "02", title: "Rééquilibrer", text: "Par le toucher, le souffle ou l'énergie, je relance la circulation de l'énergie vitale et libère les blocages." },
  { num: "03", title: "Libérer", text: "Vous repartez avec des outils simples pour prolonger le soin et cheminer vers votre liberté intérieure." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const [avis, setAvis] = useState([]);
  const [avisForm, setAvisForm] = useState({ name: "", soin: "", note: 5, message: "" });
  const [sendingAvis, setSendingAvis] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avis`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setAvis)
      .catch(() => {});
  }, []);

  const setAvisField = (key) => (e) => setAvisForm({ ...avisForm, [key]: e.target.value });

  const submitAvis = async (e) => {
    e.preventDefault();
    setSendingAvis(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...avisForm, note: Number(avisForm.note) }),
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setAvis([saved, ...avis]);
      setAvisForm({ name: "", soin: "", note: 5, message: "" });
      toast.success("Merci ! Votre avis rejoint le livre d'or.");
    } catch {
      toast.error("L'envoi a échoué. Réessayez dans un instant.");
    } finally {
      setSendingAvis(false);
    }
  };

  const avisInputCls = "w-full rounded-xl border border-olive-border bg-beige-100 px-4 py-3 text-sm text-ink placeholder:text-stone2 focus:outline-none focus:ring-2 focus:ring-olive/40 focus:border-olive transition-all";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-xs uppercase tracking-[0.3em] text-olive mb-8"
              data-testid="hero-overline"
            >
              Thérapeute & formatrice — Bayonne · Bellignat
            </motion.p>
            <LineReveal
              lines={["L'art du soin,", "la voie de", "l'équilibre."]}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-ink"
              lineClassName="text-balance"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-8 text-base sm:text-lg text-sage leading-relaxed max-w-xl"
              data-testid="hero-intro"
            >
              Médecine chinoise, ostéothérapie, sophrologie, magnétisme — une approche holistique pour rééquilibrer le corps et l'esprit, dans le respect de votre unicité à Bayonne.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-7 flex items-center gap-3"
              data-testid="hero-social-links"
            >
              <a
                href="https://www.instagram.com/juliezittel/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                aria-label="Instagram de Julie Zittel"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-olive-border bg-beige-50 text-olive transition-all duration-300 hover:bg-olive hover:text-beige-100 hover:-translate-y-0.5"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a
                href="https://www.facebook.com/julieosteotheotherapeute"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                aria-label="Facebook de Julie Zittel"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-olive-border bg-beige-50 text-olive transition-all duration-300 hover:bg-olive hover:text-beige-100 hover:-translate-y-0.5"
              >
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <span className="text-xs text-stone2 tracking-wide">Suivez-moi au fil des saisons</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-12"
              data-testid="hero-quick-soins"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-stone2 mb-4">Découvrir un soin</p>
              <div className="border-t border-olive-border">
                {QUICK_SOINS.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/soins/${s.slug}`}
                    data-testid={`quick-soin-${s.slug}`}
                    className="group flex items-center justify-between border-b border-olive-border py-4 transition-colors duration-300 hover:bg-olive-soft/50 px-2 -mx-2 rounded-sm"
                  >
                    <span className="font-serif text-xl sm:text-2xl text-ink group-hover:text-olive transition-colors duration-300">{s.name}</span>
                    <ArrowUpRight className="w-5 h-5 text-olive-light transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                  </Link>
                ))}
              </div>
              <Link to="/soins" data-testid="hero-all-soins-link" className="mt-5 inline-flex items-center gap-2 text-sm text-olive hover:text-olive-dark transition-colors">
                Voir tous les soins <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-olive-border shadow-[0_30px_60px_-30px_rgba(49,63,21,0.35)]" data-testid="hero-portrait-frame">
                <motion.img
                  src="/images/portrait-hero.jpg"
                  alt="Julie Zittel, thérapeute et magnétiseuse"
                  data-testid="hero-portrait"
                  style={{ y: imgY }}
                  className="w-full h-[420px] sm:h-[520px] object-cover scale-110"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-beige-50 border border-olive-border rounded-2xl px-6 py-5 shadow-lg max-w-[240px]">
                <p className="font-serif italic text-lg text-olive-dark leading-snug">« Je suis là pour vous, dans le cœur, avec douceur et détermination. »</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* QUI SUIS-JE */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="qui-suis-je-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-6">Présentation</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink leading-tight">Qui suis-je ?</h2>
        </Reveal>
        <div className="mt-12 grid lg:grid-cols-2 gap-x-14">
          <div>
            {QUI_SUIS_JE.slice(0, 3).map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className={`text-sage leading-relaxed ${i === 0 ? "font-serif text-2xl sm:text-[1.7rem] text-ink font-light" : "text-base sm:text-lg mt-6"}`}>{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-6 lg:mt-0">
            {QUI_SUIS_JE.slice(3).map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className={`text-sage leading-relaxed text-base sm:text-lg ${i > 0 ? "mt-6" : ""}`}>{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <p className="mt-8 font-serif italic text-2xl text-olive">Au plaisir de vous rencontrer — avec amour, Julie.</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid md:grid-cols-2 gap-10 items-center" data-testid="qui-suis-je-photo-quote">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-olive-border">
              <img src="/images/portrait.jpg" alt="Julie Zittel, thérapeute et magnétiseuse" className="w-full h-80 sm:h-96 object-cover" data-testid="qui-suis-je-image" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <blockquote className="border-l-2 border-olive pl-8" data-testid="qui-suis-je-quote">
              <p className="font-serif italic text-2xl sm:text-3xl text-olive-dark leading-relaxed">
                « La plus grande découverte de notre génération a été de s'apercevoir qu'un homme peut changer sa vie en modifiant sa façon de penser. »
              </p>
              <cite className="block mt-4 text-sm not-italic font-sans text-stone2 tracking-wide">— William James</cite>
            </blockquote>
          </Reveal>
        </div>

        {/* MANIFESTO CHAPTERS */}
        <div className="mt-24 grid md:grid-cols-3 gap-8" data-testid="manifesto-chapters">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.12}>
              <div className="group border-t-2 border-olive pt-6 transition-transform duration-500 hover:-translate-y-1" data-testid={`chapter-${c.num}`}>
                <span className="font-serif text-5xl text-olive/30 group-hover:text-olive/60 transition-colors duration-500">{c.num}</span>
                <h3 className="mt-4 font-serif text-2xl text-ink">{c.title}</h3>
                <p className="mt-3 text-sm text-sage leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SOINS INDEX */}
      <section className="py-24 bg-beige-200/50 border-y border-olive-border/60" data-testid="soins-index-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-olive mb-4">Les soins</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink">Chaque soin, une page</h2>
            </div>
            <Link to="/soins" data-testid="soins-index-all-link" className="inline-flex items-center gap-2 text-sm text-olive hover:text-olive-dark transition-colors">
              Tout le soin en détail <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="border-t border-olive-border">
            {SOINS.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link
                  to={`/soins/${s.slug}`}
                  data-testid={`soin-row-${s.slug}`}
                  className="group grid grid-cols-[auto_1fr_auto] sm:grid-cols-[3rem_1fr_auto_auto] items-center gap-4 sm:gap-8 border-b border-olive-border py-6 transition-colors duration-300 hover:bg-olive-soft/40 px-2 -mx-2 rounded"
                >
                  <span className="font-serif text-lg text-olive/40">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block font-serif text-2xl sm:text-3xl text-ink group-hover:text-olive transition-colors duration-300">{s.name}</span>
                    <span className="block text-xs text-stone2 mt-1 tracking-wide">{s.tag}</span>
                  </span>
                  <span className="hidden sm:block text-sm text-sage">{s.price} · {s.duration}</span>
                  <ArrowUpRight className="w-5 h-5 text-olive-light transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="temoignages-section">
        <Reveal className="mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-4">Témoignages après consultation</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ink max-w-2xl">Ils ont cheminé avec moi</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {avis.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}>
              <figure className="h-full bg-olive-soft/50 border border-olive-border rounded-3xl p-8 flex flex-col transition-transform duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-25px_rgba(49,63,21,0.3)]" data-testid={`avis-card-${a.id}`}>
                <div className="flex gap-1 mb-5" aria-label={`${a.note} étoiles`}>
                  {Array.from({ length: a.note }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-gold text-gold" strokeWidth={1} />
                  ))}
                </div>
                <blockquote className="font-serif text-xl leading-relaxed text-ink flex-1">« {a.message} »</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-olive-border/70">
                  <span className="block text-sm font-medium text-olive-dark">{a.name}</span>
                  {a.soin && <span className="block text-xs text-stone2 mt-1">{a.soin}</span>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
          {TEMOIGNAGES.map((t, i) => (
            <Reveal key={`temoignage-${i}`} delay={i * 0.12}>
              <figure className="h-full bg-beige-50 border border-olive-border rounded-3xl p-8 flex flex-col transition-transform duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-25px_rgba(49,63,21,0.3)]" data-testid={`temoignage-${i}`}>
                <div className="flex gap-1 mb-5" aria-label="5 étoiles">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-gold text-gold" strokeWidth={1} />
                  ))}
                </div>
                <blockquote className="font-serif text-lg leading-relaxed text-ink flex-1">« {t.quote} »</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-olive-border/70">
                  <span className="block text-sm font-medium text-olive-dark">{t.name}</span>
                  <span className="block text-xs text-stone2 mt-1">{t.soin}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-xs text-stone2">Note Google : 5/5 — 41 avis, 100 % cinq étoiles. Avis également consultables sur Google & Facebook.</p>
        </Reveal>

        {/* LIVRE D'OR */}
        <Reveal delay={0.1}>
          <div className="mt-16 bg-beige-50 border border-olive-border rounded-3xl p-8 sm:p-12" data-testid="livre-d-or">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-olive mb-4">Livre d'or</p>
                <h3 className="font-serif text-3xl sm:text-4xl font-light text-ink leading-snug">Votre expérience compte</h3>
                <p className="mt-4 text-sage leading-relaxed text-sm sm:text-base max-w-md">
                  Vous avez reçu un soin ou suivi une formation ? Quelques mots suffisent — votre avis paraît ici même, dans les témoignages, et guide celles et ceux qui hésitent encore.
                </p>
              </div>
              <form onSubmit={submitAvis} data-testid="livre-d-or-form" className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required data-testid="livre-or-name" placeholder="Votre prénom" value={avisForm.name} onChange={setAvisField("name")} className={avisInputCls} />
                  <input data-testid="livre-or-soin" placeholder="Soin reçu (facultatif)" value={avisForm.soin} onChange={setAvisField("soin")} className={avisInputCls} />
                </div>
                <select data-testid="livre-or-note" value={avisForm.note} onChange={setAvisField("note")} className={avisInputCls}>
                  <option value={5}>5 étoiles — Une parenthèse inoubliable</option>
                  <option value={4}>4 étoiles — Très belle expérience</option>
                  <option value={3}>3 étoiles — Bien</option>
                  <option value={2}>2 étoiles — Moyen</option>
                  <option value={1}>1 étoile — Décevant</option>
                </select>
                <textarea required rows={4} data-testid="livre-or-message" placeholder="Racontez votre expérience…" value={avisForm.message} onChange={setAvisField("message")} className={avisInputCls} />
                <button
                  type="submit"
                  disabled={sendingAvis}
                  data-testid="livre-or-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-olive px-7 py-3.5 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                >
                  {sendingAvis ? "Envoi en cours…" : "Publier mon avis"} <Send className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </form>
            </div>
          </div>
        </Reveal>
      </section>

      {/* BANDEAU TÉMOIGNAGES DÉFILANT */}
      <div className="border-y border-olive-border/60 bg-olive-soft/40 py-8 overflow-hidden" data-testid="temoignages-marquee">
        <Marquee speed={25} gradient={false} pauseOnHover>
          {TEMOIGNAGES_DEFILANT.map((t, i) => (
            <figure key={i} className="mx-5 w-[320px] sm:w-[400px] shrink-0 bg-beige-50 border border-olive-border rounded-2xl px-7 py-6" data-testid={`marquee-temoignage-${i}`}>
              <div className="flex gap-1 mb-3" aria-label="5 étoiles">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="w-3.5 h-3.5 fill-gold text-gold" strokeWidth={1} />
                ))}
              </div>
              <blockquote className="font-serif text-lg italic text-ink leading-relaxed">« {t.quote} »</blockquote>
              <figcaption className="mt-3 text-xs tracking-wide text-stone2">— {t.name}</figcaption>
            </figure>
          ))}
        </Marquee>
      </div>

      {/* CAPTURE EMAILS — GUIDE OFFERT */}
      <NewsletterSignup source="accueil" />

      {/* CTA */}
      <section className="relative overflow-hidden bg-olive-dark text-beige-100" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight">Prendre soin de vous commence ici.</h2>
            <p className="mt-6 text-beige-300/90 leading-relaxed max-w-md">Une question, une envie de soin, une réservation ? Écrivez-moi — je vous réponds avec le cœur.</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:justify-self-end">
            <div className="flex flex-col gap-5">
              <Link
                to="/contact"
                data-testid="home-cta-contact-button"
                className="inline-flex w-fit items-center rounded-full bg-beige-100 px-8 py-4 text-sm font-medium text-olive-dark transition-all duration-300 hover:bg-beige-50 hover:-translate-y-0.5"
              >
                Me contacter
              </Link>
              <div className="flex flex-col gap-2 text-sm text-beige-300/80">
                {CABINETS.map((c) => (
                  <span key={c.city} className="inline-flex items-center gap-2">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} /> {c.address}, {c.zip}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
